// Co-Authored-By: AI-Assisted | updated: 2026-10-21 15:45:00
import { useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'antd';
import { STATIC_RESOURCE } from '@/utils/tools';
import { storage } from '@/utils/storage';
import { useI18n } from '@/i18n';
import { companyRouter } from '@/routes/companyRouter';
import { buyersRouters } from '@/routes/buyersRouters';
import { supplierRouter } from '@/routes/supplierCenterRouters';
import { inviteRouters } from '@/routes/inviteRouter';
import './index.less';

const APPLY_ICON = `${STATIC_RESOURCE}/cp_gsp/header/applyToMar.png`;
const DEFAULT_ICON = `${STATIC_RESOURCE}/cp_gsp/header/supplierMar.png`;
// 二级菜单图标，按路由 key 配置
const ICONS = {
  companyManagement: `${STATIC_RESOURCE}/cp_gsp/header/companyMgr.png`, // 公司管理
  staffManagement: `${STATIC_RESOURCE}/cp_gsp/header/userMar.png`, // 员工账号
  contractManagement: `${STATIC_RESOURCE}/cp_gsp/header/contarctMar.png`, // 合同管理
  settlementManagement: `${STATIC_RESOURCE}/cp_gsp/header/settlementMar.png`, // 结算管理
  inquiry: `${STATIC_RESOURCE}/cp_gsp/header/inquiryPurMar.png`, // 询价采购
  supplier: `${STATIC_RESOURCE}/cp_gsp/header/supplierMar.png`, // 供应商管理
  tender: `${STATIC_RESOURCE}/cp_gsp/header/tenderMar.png`, // 招投标管理
  priceCenter: `${STATIC_RESOURCE}/cp_gsp/header/priceMar.png`, // 价格中心
  orderManagement: `${STATIC_RESOURCE}/cp_gsp/header/orderMar.png`, // 订单管理
  quotationManagement: `${STATIC_RESOURCE}/cp_gsp/header/inquiryQuoMar.png`, // 询价报价
  bid: `${STATIC_RESOURCE}/cp_gsp/header/tenderMar.png`, // 招投标管理
  supplierPriceCenter: `${STATIC_RESOURCE}/cp_gsp/header/priceMar.png`, // 价格中心
  invitenotice: `${STATIC_RESOURCE}/cp_gsp/header/biddingAnnMar.png`, // 竞价招标公告
  invite: `${STATIC_RESOURCE}/cp_gsp/header/biddingInvMar.png`, // 竞价招标
  inviteentry: `${STATIC_RESOURCE}/cp_gsp/header/biddingSubMar.png`, // 竞价投标
};

const PORTAL_CONFIG = [
  { key: 'company', title: 'Company Management', rootKey: 'companyManagement', routes: companyRouter, i18n: 'pages.company.index.language' },
  { key: 'buyers', title: 'Buyer Center', rootKey: 'buyersIndex', routes: buyersRouters, i18n: 'pages.buyers.language', cpgroupFilter: true, excludeKeys: ['buyersIndex', 'planManagement', 'importDocuments'] },
  { key: 'supplier', title: 'Supplier Center', rootKey: 'SellIndex', routes: supplierRouter, i18n: 'pages.supplierCenter.language', cpgroupFilter: true, supplierOnly: true, excludeKeys: ['SellIndex', 'planManagementSupplier', 'importDocumentsSupplier', 'ProductRecommendation'] },
  { key: 'invite', title: 'Tender Center', rootKey: 'invitenotice', routes: inviteRouters, i18n: 'pages.invitePro.language', labels: { invitenotice: 'Competitive Bidding Announcement', invite: 'Competitive Bidding', inviteentry: 'Competitive Bidding Submission' } },
];

// companyManagement: 未加入公司时跳 /apply（与头部用户下拉「公司管理」一致）
// supplier: 非采购商时跳 /apply（与首页买家服务「供应商管理」一致）
const getFallbackPath = (key, { company_id, isPurchaser }) => {
  if (key === 'companyManagement' && !company_id) return '/apply';
  if (key === 'supplier' && !isPurchaser) return '/apply';
  return undefined;
};

const resolvePath = (routes, targetKey, menuKeys) => {
  const canNavigate = route => !route.hidden && route.path && !route.path.includes(':');
  const findRoute = (list) => {
    for (const route of list) {
      if (route.key === targetKey) return route;
      const found = route.children && findRoute(route.children);
      if (found) return found;
    }
    return undefined;
  };
  const firstPath = (list = [], checkAuth) => {
    for (const route of list) {
      if (route.hidden || (checkAuth && !menuKeys.has(route.key))) continue;
      if (canNavigate(route)) return route.path;
      const path = route.children && firstPath(route.children, checkAuth);
      if (path) return path;
    }
    return undefined;
  };
  const target = findRoute(routes);
  if (!target || target.hidden || !menuKeys.has(target.key)) return undefined;
  if (canNavigate(target)) return target.path;
  return firstPath(target.children, true) || firstPath(target.children, false);
};

// isSupplier: 是否正式供应商；applyText: 申请/查看审批进度文案；onApply: 沿用原供应商申请逻辑
export default function PortalMenu({ isSupplier, applyText, onApply, onOpenChange }) {
  const $t = useI18n('component.header.language');
  const $tCompany = useI18n('pages.company.index.language');
  const $tBuyers = useI18n('pages.buyers.language');
  const $tSupplier = useI18n('pages.supplierCenter.language');
  const $tInvite = useI18n('pages.invitePro.language');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menus = useSelector(state => state.menus.list || [], shallowEqual);
  const { group_type } = storage.getJSON('company_info', {});
  const { company_id } = useSelector(state => state.lang.user_info) || {};
  const company_info = useSelector(state => state.lang.company_info);

  const columns = useMemo(() => {
    const isPurchaser = !!(company_info?.company_role & 2); // 采购商身份
    const allKeys = new Set(menus.map(item => item.key));
    const groupKeys = new Set(menus.filter(item => !(item.is_cpgroup && group_type === 0)).map(item => item.key));
    const translators = { 'pages.company.index.language': $tCompany, 'pages.buyers.language': $tBuyers, 'pages.supplierCenter.language': $tSupplier, 'pages.invitePro.language': $tInvite };
    return PORTAL_CONFIG.map(({ rootKey, routes, i18n, labels = {}, cpgroupFilter, supplierOnly, excludeKeys = [], ...column }) => {
      const menuKeys = cpgroupFilter ? groupKeys : allKeys;
      if (!menuKeys.has(rootKey)) return null;
      // 供应商中心：再判断是否正式供应商（ companyRole === 1），不是则只展示申请入口
      if (supplierOnly && !isSupplier) return { ...column, items: [], showApply: true };
      const path = resolvePath(routes, rootKey, menuKeys);
      const t = translators[i18n] || (label => label);
      const items = routes
        .filter(route => route.key && !route.hidden && !excludeKeys.includes(route.key))
        .map(route => ({
          key: route.key,
          label: t(labels[route.key] || route.label),
          icon: ICONS[route.key] || DEFAULT_ICON,
          path: (menuKeys.has(route.key) && getFallbackPath(route.key, { company_id, isPurchaser })) || resolvePath(routes, route.key, menuKeys),
        }))
        .filter(item => item.path);
      if (!items.length) return null;
      return { ...column, path, items };
    }).filter(Boolean);
  }, [menus, group_type, company_id, company_info, isSupplier, $tCompany, $tBuyers, $tSupplier, $tInvite]);

  const handleOpenChange = (value) => {
    setOpen(value);
    if (value) onOpenChange?.();
  };

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const apply = () => {
    setOpen(false);
    onApply?.();
  };

  const panel = (
    <div className='portal-menu-panel'>
      {columns.map(column => (
        <div className='portal-menu-column' key={column.key}>
          <div className={`portal-menu-title${column.path ? ' portal-menu-title-link' : ''}`} onClick={column.path ? () => go(column.path) : undefined}>{$t(column.title)}</div>
          <div className='portal-menu-items'>
            {column.items.map(item => (
              <div className='portal-menu-item' key={item.key} onClick={() => go(item.path)}>
                <span className='portal-menu-icon'><img src={item.icon} width={14} height={14} alt={``} /></span>{item.label}
              </div>
            ))}
            {column.showApply && applyText ? (
              <div className='portal-menu-item portal-menu-apply' onClick={apply}>
                <span className='portal-menu-icon'><img src={APPLY_ICON} width={14} height={14} alt={``} /></span>{applyText}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Dropdown disabled={!columns.length} open={open && !!columns.length} onOpenChange={handleOpenChange} popupRender={() => panel} placement='bottom'>
      <span className='portal-menu-trigger'>
        <img src={`${STATIC_RESOURCE}/cp_gsp/header/workoutlined.png`} width={20} height={20} alt='' />
      </span>
    </Dropdown>
  );
}
